import { apiClient } from "./apiClient";

export type PositionStatus = "active" | "pending" | "orphaned" | "stuck" | "ghost";
export type AccountType = "admin" | "subscriber";
export type PositionAction = "send_exit" | "force_close" | "delete_redis" | "delete_db" | "import_to_system" | "mark_closed" | "close_exchange" | "clean_all";

export interface PositionRow {
  position_id: string;
  account_id: string;
  symbol: string;
  entry_price: number;
  quantity: number;
  current_price?: number;
  pnl?: number;
  status: PositionStatus;
  status_reason: string;
  linked_signal_id?: string;
  exit_attempts: number;
  created_at?: string;
  available_actions: PositionAction[];
  data_sources: string[];
}

export interface UserSection {
  user_id?: string;
  user_email: string;
  account_type: AccountType;
  account_id: string;
  account_nickname?: string;
  total_pnl: number;
  positions_count: number;
  positions: PositionRow[];
}

export interface PositionsMonitorStats {
  total_positions: number;
  active_positions: number;
  pending_positions: number;
  orphaned_positions: number;
  stuck_positions: number;
  ghost_positions: number;
}

export interface PositionsMonitorResponse {
  admin_positions: UserSection[];
  subscriber_positions: UserSection[];
  stuck_positions: PositionRow[];
  stats: PositionsMonitorStats;
}

export interface ActionRequest {
  action: PositionAction;
  reason?: string;
  data_sources?: string[];
}

export interface ActionResponse {
  success: boolean;
  message: string;
  details?: Record<string, unknown>;
}

export async function getPositionsMonitor(params?: {
  symbol?: string;
  status?: PositionStatus;
  account_type?: AccountType;
}): Promise<PositionsMonitorResponse> {
  try {
    const response = await apiClient.get<PositionsMonitorResponse>(
      "/admin/exit-signals/positions-monitor",
      { params }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get positions monitor: ${error?.response?.status || "Unknown error"}`;
    throw new Error(errorMessage);
  }
}

export async function executePositionAction(
  positionId: string,
  action: PositionAction,
  reason?: string,
  dataSources?: string[]
): Promise<ActionResponse> {
  try {
    const response = await apiClient.post<ActionResponse>(
      `/admin/exit-signals/positions-monitor/action`,
      { action, reason, data_sources: dataSources } as ActionRequest,
      { params: { position_id: positionId } }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to execute action: ${error?.response?.status || "Unknown error"}`;
    throw new Error(errorMessage);
  }
}

export const ACTION_LABELS: Record<PositionAction, string> = {
  send_exit: "Send Exit",
  force_close: "Force Close",
  delete_redis: "Delete Redis",
  delete_db: "Delete DB",
  import_to_system: "Import",
  mark_closed: "Mark Closed",
  close_exchange: "Close Exchange",
  clean_all: "Clean All Sources",
};

export const ACTION_COLORS: Record<PositionAction, string> = {
  send_exit: "primary",
  force_close: "danger",
  delete_redis: "warning",
  delete_db: "danger",
  import_to_system: "success",
  mark_closed: "secondary",
  close_exchange: "danger",
  clean_all: "danger",
};

export const STATUS_LABELS: Record<PositionStatus, string> = {
  active: "Active",
  pending: "Pending Exit",
  orphaned: "Orphaned",
  stuck: "Stuck",
  ghost: "Ghost",
};

export const STATUS_COLORS: Record<PositionStatus, string> = {
  active: "#10b981",
  pending: "#f59e0b",
  orphaned: "#8b5cf6",
  stuck: "#ef4444",
  ghost: "#6b7280",
};

export const SOURCE_LABELS: Record<string, string> = {
  redis: "Redis",
  user_position_db: "UserPosition DB",
  trade_log_db: "TradeLog DB",
  stuck_detection: "Stuck Detection",
  recently_orphaned: "Recently Orphaned",
  dead_exit_jobs: "Dead Exit Jobs",
  bybit: "Bybit",
};

export const SOURCE_COLORS: Record<string, string> = {
  redis: "#3b82f6",
  user_position_db: "#f59e0b",
  trade_log_db: "#8b5cf6",
  stuck_detection: "#ef4444",
  recently_orphaned: "#6b7280",
  dead_exit_jobs: "#dc2626",
  bybit: "#10b981",
};

// ─── Dust Conversion Types ─────────────────────────────────────

export interface ConvertDustRequest {
  account_ids?: string[];
}

export interface AccountConvertResult {
  account_id: string;
  account_nickname?: string;
  success: boolean;
  coins_converted: number;
  coins_remaining: number;
  converted_usdt_value: number;
  remaining_usdt_value: number;
  details?: Record<string, unknown>[];
  error?: string;
}

export interface ConvertDustResponse {
  success: boolean;
  total_accounts: number;
  successful_accounts: number;
  failed_accounts: number;
  total_coins_converted: number;
  total_coins_remaining: number;
  total_converted_usdt_value: number;
  total_remaining_usdt_value: number;
  results: AccountConvertResult[];
  message: string;
}

export async function convertUsersDust(
  accountIds?: string[]
): Promise<ConvertDustResponse> {
  try {
    const response = await apiClient.post<ConvertDustResponse>(
      "/admin/exit-signals/convert-dust",
      { account_ids: accountIds } as ConvertDustRequest
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to convert dust: ${error?.response?.status || "Unknown error"}`;
    throw new Error(errorMessage);
  }
}

// ─── Dust Account Options (User Selection Modal) ───────────────

export interface DustAccountOption {
  account_id: string;
  account_nickname?: string;
  owner?: string;
}

export interface DustUserOption {
  user_id: string;
  email: string;
  name?: string;
  role: string;
  accounts: DustAccountOption[];
}

export interface DustAccountsListResponse {
  admins: DustUserOption[];
  subscribers: DustUserOption[];
  total_accounts: number;
}

export async function getDustAccountOptions(): Promise<DustAccountsListResponse> {
  try {
    const response = await apiClient.get<DustAccountsListResponse>(
      "/admin/exit-signals/dust-account-options"
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to fetch dust account options: ${error?.response?.status || "Unknown error"}`;
    throw new Error(errorMessage);
  }
}

// ---------------------------------------------------------------------------
// Subscriber Positions — lightweight endpoint for the Subscriber Positions tab
// ---------------------------------------------------------------------------

export type SubscriberSourceFilter = "all" | "redis" | "user_position_db" | "trade_log_db" | "bybit";

export interface SubscriberPositionRow extends PositionRow {
  notional_value: number;
  unit_value: number;
  opened_by: string;
  owner_email: string;
  account_nickname?: string;
}

export interface SubscriberPositionsResponse {
  positions: SubscriberPositionRow[];
  total_count: number;
}

export const SOURCE_FILTER_LABELS: Record<SubscriberSourceFilter, string> = {
  all: "All Sources",
  redis: "Redis",
  user_position_db: "UserPosition DB",
  trade_log_db: "TradeLog DB",
  bybit: "Bybit",
};

export async function getSubscriberPositions(params?: {
  symbol?: string;
  source?: SubscriberSourceFilter;
  min_value?: number;
  user_email?: string;
}): Promise<SubscriberPositionsResponse> {
  try {
    const response = await apiClient.get<SubscriberPositionsResponse>(
      "/admin/exit-signals/subscriber-positions",
      { params }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get subscriber positions: ${error?.response?.status || "Unknown error"}`;
    throw new Error(errorMessage);
  }
}
